import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, Alert } from '@mui/material';
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { addPaymentMethod } from '../api/paymentApi';
import usePaymentStore from '../../../store/paymentStore';

// Load Stripe outside of component render cycle (would be stored in a separate file)
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_51Qoix3KoczsQOoB4YvsPWn5b0U6WPBfcht2GXDDbRRjilxgNbfaoQx12jiOeV1WXP3W2ny7wOpy25UUsv08SrFxJ00hwEI4tlJ');

// Card element styles
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

interface AddPaymentMethodFormProps {
  onSuccess: () => void;
}

// Actual form component that receives Stripe context
const PaymentMethodFormContent: React.FC<AddPaymentMethodFormProps> = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { isLoading, error } = usePaymentStore();
  
  const [cardError, setCardError] = useState<string | null>(null);

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      console.error('Stripe.js has not loaded yet');
      setCardError('Payment processing not available. Please try again later.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    
    if (!cardElement) {
      console.error('Card element not found');
      setCardError('Payment form not properly loaded. Please refresh the page and try again.');
      return;
    }

    try {
      // Create payment method
      console.log('Creating payment method...');
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        console.error('Stripe error:', error);
        setCardError(error.message || 'An error occurred with your card');
        return;
      }

      console.log('Payment method created, ID:', paymentMethod.id.substring(0, 5) + '...');
      
      try {
        // Pass the payment method ID to your server
        console.log('Sending payment method to server...');
        await addPaymentMethod(paymentMethod.id);
        console.log('Payment method successfully added');
        onSuccess();
      } catch (err: any) {
        console.error('Server error when adding payment method:', err);
        setCardError(err.message || 'Server error when adding payment method. Please try again.');
      }
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setCardError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {cardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cardError}
        </Alert>
      )}
      
      <Typography variant="subtitle1" gutterBottom>
        Card Information
      </Typography>
      
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 3,
        }}
      >
        <CardElement options={cardElementOptions} />
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        Your card information is secured with industry-standard encryption.
      </Typography>
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={!stripe || isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : 'Add Payment Method'}
      </Button>
    </Box>
  );
};

// Wrapper component that provides Stripe context
const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodFormContent {...props} />
    </Elements>
  );
};

export default AddPaymentMethodForm;
